/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { INileSubscribeConfig, INileSubscribeProps } from './nile-subscribe.props.autogenerated';
import { subscribeToMailChimpAsync } from '../../actions/DataActionExtension.g';
import { Alert } from 'reactstrap';

interface ISubscribeState {
    showResponseMessage: boolean;
    isError: boolean;
    inputValue: string;
}

/**
 * Subscribe view props.
 */
export interface ISubscribeViewProps extends INileSubscribeProps<INileSubscribeConfig> {
    subscribe: IModuleProps;
    subscribeContainer: INodeProps;
    heading: React.ReactNode;
    text: React.ReactNode;
    submitButton?: INodeProps;
    submitButtonLabelText?: string;
    subscribeForm?: INodeProps;
    emailInput?: INodeProps;
    handleText(): void;
    handleSubscribe(): void;
    responseMessage: React.ReactNode;
}

/**
 * Subscribe component.
 * @extends {React.PureComponent<INileSubscribeProps<INileSubscribeData>>}
 */
export class Subscribe extends React.PureComponent<INileSubscribeProps<INileSubscribeConfig>, ISubscribeState> {
    private readonly _emailAddressInputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ISubscribeViewProps) {
        super(props);
        const {
            context: {
                request: {
                    user: { emailAddress }
                }
            }
        } = props;
        this._emailAddressInputRef = React.createRef();

        this.state = {
            showResponseMessage: false,
            isError: false,
            inputValue: emailAddress || ''
        };
        this.hideResponseMessage = this.hideResponseMessage.bind(this);
    }

    private hideResponseMessage() {
        this.setState({ showResponseMessage: false });
    }

    public render(): JSX.Element | null {
        const { heading, paragraph } = this.props.config;

        const moduleClassName = 'ms-subscribe';

        const doSubscribe = async () => {
            const {
                context: { actionContext },
                config: { useDoubleOptIn, audience }
            } = this.props;
            const { inputValue } = this.state;
            if (inputValue) {
                try {
                    const response = await subscribeToMailChimpAsync(
                        { callerContext: actionContext },
                        inputValue,
                        useDoubleOptIn ?? false,
                        audience
                    );
                    this.setState({
                        showResponseMessage: true,
                        isError: !response.Status
                    });
                } catch (exception) {
                    this.setState({
                        showResponseMessage: true,
                        isError: true
                    });
                }
            }
        };

        const renderSubscriptionResultMessage = () => {
            const { showResponseMessage, isError } = this.state;
            const {
                config: { useDoubleOptIn },
                resources: { subscriptionSuccessMessage, subscriptionOptInMessage, subscriptionFailMessage }
            } = this.props;
            if (!showResponseMessage) {
                return null;
            }
            if (isError) {
                return <span className='msc-alert msc-alert-danger'>{subscriptionFailMessage}</span>;
            } else if (useDoubleOptIn) {
                return <span className='msc-alert'>{subscriptionOptInMessage}</span>;
            } else {
                return <span className='msc-alert'>{subscriptionSuccessMessage}</span>;
            }
        };

        const renderSubscriptionResultAlert = () => {
            const { showResponseMessage, isError } = this.state;

            return (
                <Alert color={isError ? 'error' : 'success'} isOpen={showResponseMessage} toggle={this.hideResponseMessage}>
                    {renderSubscriptionResultMessage()}
                </Alert>
            );
        };

        const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({ inputValue: e.target.value });
        };

        const subscribeParagraph = paragraph && (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className='ms-subscribe__details__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );

        const subscribeHeading = heading && (
            <Msdyn365.Text
                className='ms-subscribe__details__heading'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );
        const viewProps = {
            ...this.props,
            handleText: this.handleTextChange,
            handleSubscribe: doSubscribe,
            subscribe: {
                moduleProps: this.props,
                className: moduleClassName
            },
            subscribeContainer: {
                className: 'ms-subscribe__details'
            },
            heading: subscribeHeading,
            text: subscribeParagraph,
            subscribeForm: {
                className: 'ms-subscribe__details__form',
                tag: 'form',
                'aria-label': this.props.resources.subscribeFormAriaLabel,
                name: 'subscribeForm',
                role: 'form',
                autoComplete: 'off',
                onSubmit: doSubscribe
            },
            emailInput: {
                className: 'ms-subscribe__details__form__email',
                tag: 'input',
                ref: this._emailAddressInputRef,
                type: 'email',
                'aria-label': this.props.resources.subscribeFormEmailAriaLabel,
                placeholder: this.props.resources.subscribeEmailPlaceholderText,
                value: this.state.inputValue,
                onChange: handleInputValueChange
            },
            submitButton: {
                className: 'ms-subscribe__details__form__submit',
                tag: Button,
                'aria-label': this.props.resources.subscribeFormSubmitAriaLabel,
                type: 'submit',
                title: this.props.resources.subscribeButtonLabel
            },
            submitButtonLabelText: this.props.resources.subscribeButtonLabel,
            responseMessage: renderSubscriptionResultAlert(),
            inputValue: this.state.inputValue
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };
}

export default Subscribe;
